import { useState, useEffect } from 'react';

const validateField = (values, validatorFunction, fieldName) => {
    const validatorFunctions = validatorFunction ? [].concat(validatorFunction) : [];

    const validation = validatorFunctions.reduce((prev, currentFunction) => {
        if (!currentFunction) return prev;
        const currentValidation = currentFunction(values[fieldName], values, fieldName);
        return (
            {
                valid: prev.valid && currentValidation.valid,
                errorTextKey: prev.valid ? currentValidation.errorTextKey : prev.errorTextKey,
            }
        );
    }, { valid: true });

    return validation.valid ? null : validation.errorTextKey;
};

const validateAllFields = (values, validators) => Object.keys(validators).reduce(
    (prev, key) => ({ ...prev, [key]: validateField(values, validators[key], key) }),
    {},
);

const checkHasErrors = errors => Object.keys(errors).reduce(
    (prev, key) => prev || (errors[key] !== null),
    false,
);

const useForm = (onSubmit, validations, defaultValues = {}) => {
    const [formValues, setValues] = useState(defaultValues);
    const [errors, setErrors] = useState({});
    const [hasErrors, setHasErrors] = useState(false);
    const [shouldValidate, setShouldValidate] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);


    useEffect(() => {
        setHasErrors(checkHasErrors(errors));
    }, [errors]);

    const handleSubmit = (event) => {
        if (event) event.preventDefault();
        const validationErrors = validateAllFields(formValues, validations);

        if (!checkHasErrors(validationErrors)) {
            onSubmit(formValues, setValues, setHasUnsavedChanges);
        }

        setErrors(validationErrors);
        setShouldValidate(true);
    };

    const handleChange = name => (event) => {
        const value = event.target.value;
        setValues(prevValues => ({ ...prevValues, [name]: value }));
        setHasUnsavedChanges(true);

        if (shouldValidate) {
            const err = validateField({ [name]: value }, validations[name], name);
            setErrors(prevErrors => ({ ...prevErrors, [name]: err }));
        }
    };

    const handleCheckedChange = name => (event) => {
        handleChange(name)({ target: { value: event.target.checked } });
    };

    const resetForm = () => {
        setHasUnsavedChanges(false);
        setValues(defaultValues);
    };

    return {
        handleChange,
        handleCheckedChange,
        handleSubmit,
        formValues,
        errors,
        hasErrors,
        hasUnsavedChanges,
        resetForm,
    };
};

export default useForm;
